// Hideo - script editor's writing surface: the link bubble (#linkBubble) with Open, Edit, Copy, Remove (a click on a
// link; Ctrl/Cmd+click opens it directly: typing.ts). Ported from the legacy editor/editor.js with the same HTML, except
// that the buttons carry their action (data-act) instead of inline handlers: installLinkBubble adds the bubble's own
// listeners.
import { t } from '../../../i18n'
import { esc } from '../../../utils/html'
import { uiAlert, uiPrompt } from '../../../stores/notices'
import { useEditorStore } from '../../../stores/editor'
import { fixLinks } from '../html'
import { linkHref } from '../paste'
import { byId, queueScriptSave } from './core'

let bubbleLink: HTMLAnchorElement | null = null
/** Opens a link of the page in a new tab (only a valid address) */
export function openLinkHref(a: HTMLAnchorElement): void { const h = linkHref(a.getAttribute('href')); if (h) window.open(h, '_blank', 'noopener') }
export function showLinkBubble(a: HTMLAnchorElement): void {
  bubbleLink = a
  const b = byId('linkBubble'), h = a.getAttribute('href') || '', ok = linkHref(h)
  const btn = (act: string, icon: string, title: string) => `<button type="button" data-act="${act}" title="${title}" class="tb"><i class="fas ${icon}"></i></button>`
  b.innerHTML = (ok ? `<a href="${esc(ok)}" target="_blank" rel="noopener" data-act="hide" title="${t('Open in a new tab')}" class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-darkbtn text-xs font-semibold text-brand min-w-0 max-w-[15rem]"><i class="fas fa-arrow-up-right-from-square"></i><span class="truncate">${esc(h.replace(/^https?:\/\/(www\.)?/i, ''))}</span></a>`
    : `<span class="px-2.5 text-xs text-gray-500">${t('Invalid link')}</span>`)
    + '<span class="w-px h-5 bg-gray-800 mx-0.5 flex-shrink-0"></span>'
    + btn('edit', 'fa-pen', t('Edit link')) + btn('copy', 'fa-copy', t('Copy address')) + btn('unlink', 'fa-link-slash', t('Remove link'))
  b.classList.remove('hidden'); placeLinkBubble()
}
/** Under the link (above it near the bottom); it follows the page's scrolling (ScriptEditor.vue) and the window */
export function placeLinkBubble(): void {
  const b = byId('linkBubble')
  if (!bubbleLink || b.classList.contains('hidden')) return
  if (!bubbleLink.isConnected) return hideLinkBubble()
  const r = bubbleLink.getBoundingClientRect(), h = b.offsetHeight, w = b.offsetWidth
  b.style.top = (r.bottom + h + 12 > innerHeight ? r.top - h - 6 : r.bottom + 6) + 'px'
  b.style.left = Math.max(8, Math.min(r.left, innerWidth - w - 8)) + 'px'
}
export function hideLinkBubble(): void { byId('linkBubble').classList.add('hidden'); bubbleLink = null }
export const linkBubbleShown = () => !byId('linkBubble').classList.contains('hidden')
export async function editBubbleLink(): Promise<void> {
  const a = bubbleLink
  hideLinkBubble()
  if (!a) return
  const v = await uiPrompt(t('Link address (URL):'), a.getAttribute('href') || '', { ok: t('Save'), icon: 'fa-link' })
  if (v == null) return
  const h = linkHref(v)
  if (!h) { uiAlert(t('Invalid address: it must start with http://, https:// or www.')); return }
  a.href = h; fixLinks(); queueScriptSave()
}
/** Copies the address; the status at the top says so */
export function copyBubbleLink(): void {
  const a = bubbleLink
  hideLinkBubble()
  if (a) void navigator.clipboard.writeText(a.getAttribute('href') || '').then(() => { useEditorStore().status = t('Link copied') })
}
export function unlinkBubble(): void {
  const a = bubbleLink
  hideLinkBubble()
  if (!a) return
  a.replaceWith(...a.childNodes); queueScriptSave()
}

/** The bubble's own listeners (they replace the inline handlers, with the same effects): mousedown on a button keeps
 *  the page's selection (preventDefault); a click on a button runs its action, a click on the address hides the bubble
 *  (the browser opens the link in a new tab) */
export function installLinkBubble(): void {
  const b = byId('linkBubble')
  const ACTS: Record<string, () => unknown> = { edit: editBubbleLink, copy: copyBubbleLink, unlink: unlinkBubble, hide: hideLinkBubble }
  b.addEventListener('mousedown', e => { const x = (e.target as Element).closest('button'); if (x && b.contains(x)) e.preventDefault() })
  b.addEventListener('click', e => {
    const x = (e.target as Element).closest<HTMLElement>('[data-act]')
    if (x && b.contains(x) && ACTS[x.dataset.act as string]) void ACTS[x.dataset.act as string]()
  })
}
